import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categoryList, categoryGames } from '@/mocks/category';
import { getCategorySchemas } from '@/mocks/schema/category';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const activeCategory = slug || 'all';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const filteredGames = useMemo(() => {
    if (activeCategory === 'all') return categoryGames;
    return categoryGames.filter((g) => g.category === activeCategory);
  }, [activeCategory]);

  const currentCategory = useMemo(() => {
    const cat = categoryList.find((c) => c.id === activeCategory);
    return {
      name: cat?.name || '全部分类',
      slug: activeCategory,
    };
  }, [activeCategory]);

  const schemas = useMemo(
    () => getCategorySchemas(currentCategory.slug, currentCategory.name),
    [currentCategory]
  );

  return (
    <>
      <title>{currentCategory.name} - 2pg·com 游戏分类 | 快速开户注册</title>
      <meta
        name="description"
        content={`2pg·com ${currentCategory.name}，海量游戏资源，快速开户注册，安全加密保障，随时随地畅享优质游戏体验。`}
      />
      <link rel="canonical" href={`https://2pg.com/category/${currentCategory.slug}`} />
      <SchemaOrg schemas={schemas} />

      <Navbar />
      <main className="pt-16">
        <section className="relative h-[280px] md:h-[320px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20premium%20dark%20entertainment%20background%20with%20scattered%20colorful%20game%20icons%20and%20subtle%20golden%20glow%20particles%2C%20luxurious%20gaming%20catalog%20atmosphere%2C%20deep%20rich%20tones%20with%20warm%20amber%20highlights%2C%20sophisticated%20abstract%20composition&width=1600&height=500&seq=category-hero&orientation=landscape"
              alt="游戏分类"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 pb-10">
            <nav className="text-sm mb-3" aria-label="面包屑导航">
              <ol className="flex items-center gap-2 text-white/70">
                <li itemScope itemType="https://schema.org/ListItem">
                  <Link itemProp="item" to="/" className="hover:text-white transition-colors cursor-pointer">
                    <span itemProp="name">首页</span>
                  </Link>
                  <meta itemProp="position" content="1" />
                </li>
                <li className="text-white/40">/</li>
                <li itemScope itemType="https://schema.org/ListItem" className="text-white">
                  <span itemProp="name">{currentCategory.name}</span>
                  <meta itemProp="position" content="2" />
                </li>
              </ol>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-white">
              游戏分类 — {currentCategory.name}
            </h1>
          </div>
        </section>

        <section className="py-10 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-8">
              {categoryList.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    navigate(`/category/${cat.id}`);
                  }}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                    activeCategory === cat.id
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </button>
              ))}
            </div>

            <p className="text-sm text-foreground-500 mb-6">
              当前分类共 <span className="font-semibold text-foreground-700">{filteredGames.length}</span> 款游戏
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {filteredGames.map((game) => (
                <div
                  key={game.id}
                  className="bg-background-100 rounded-2xl overflow-hidden hover:bg-background-200/70 transition-colors group"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <h3 className="text-base font-bold font-heading text-foreground-900 mb-1.5">{game.name}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-3">{game.description}</p>
                    <div className="flex items-center gap-1.5 text-xs text-accent-600">
                      <i className="ri-user-line"></i>
                      <span>{game.players}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}